import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { prepareBakedScene, disposeTree } from './bakedModel.jsx';
import { muxMp4 } from './mp4Mux.jsx';

// «فیلمِ طرح» برای پروفایل/اکسپلور: یک فیلمِ کوتاهِ عمودی (۹:۱۶) از خودِ مدلِ سه‌بعدیِ رندرشده، روی خودِ گوشی
// و هنگامِ «ارسال فایل روی سرور» ضبط می‌شود. در شبکه‌ی پروفایل به‌جای سه‌بعدیِ زنده (که برای ده‌ها طرحِ هم‌زمان
// خیلی سنگین است) همین فیلم بی‌صدا و تکرارشونده پخش می‌شود.
//
// سناریو، مثلِ کلیپ‌های تبلیغاتیِ املاک (هر نما یک قسمت، با برشِ نرم بینِ نماها):
//   ۱) نمای پهپادی: سقف برداشته، دوری آرام دورِ کلِ خانه
//   ۲) رد شدن از هر در و بازشو و ورود به فضای بعدی (اول درِ ورودی، بعد به ترتیبِ نزدیکی)
//   ۳) در هر اتاق «نگاهِ کامل» — همان کاری که آدم در یک خانه‌ی تازه می‌کند: دوربین به سمتِ یک گوشه می‌رود و
//      وقتی به آن رسید برمی‌گردد و از همان گوشه به کلِ فضا نگاه می‌کند. اتاق‌های بزرگ از دو گوشه‌ی روبه‌رو
//   ۴) برگشت به نمای پهپادی — دقیقاً همان فریمِ اول، پس تکرار بی‌پرش است
//
// recordDesignFilm({ glb | modelUrl, lightmap (Blob) | lightmapUrl, intensity, plan, angles, onCanvas, onProgress })
//   → { blob, type, ext, segments } یا null (اگر مرورگر ضبطِ ویدیو را پشتیبانی نکند)
// angles: شناسه‌های نماهایی که کاربر تیک زده (FILM_ANGLES) — نبود = پیش‌فرض‌ها
// segments: [{ id, label, t0, t1 }] — هر نما کجای فیلم است (برای باکس‌های کوچکِ صفحه‌ی «ساخت فایل جدید»)
//
// ساختن: اگر مرورگر WebCodecs دارد (اندروید کروم، آیفون ۱۶٫۴+) فیلم «فریم‌به‌فریم» ساخته می‌شود — هر فریم دقیقاً
// ۱/۳۰ ثانیه، هر قدر هم گوشی برای کشیدنِ یک فریم کند باشد. پس عوض شدنِ نما (بارگذاریِ بافت‌ها، سقف و …) دیگر در
// فیلم «گیر» نمی‌اندازد. وگرنه همان ضبطِ زمانِ واقعی (MediaRecorder)، با گرم کردنِ همه‌ی نماها قبل از ضبط

const WIDTH = 720;
const HEIGHT = 1280;
const FPS = 30;
const BITRATE = 2_000_000;
const EYE = 1.55; // متر
const INTRO_S = 4;
const OUTRO_S = 3;
const CLIP_S = 3.2; // هر نمای «رد شدن از در»
const MAX_CLIPS = 7;
const FADE_S = 0.28; // برشِ نرم (سفید) بینِ نماها
const BEFORE_M = 1.7; // شروعِ نما: این‌قدر قبل از در
const AFTER_M = 2.1; // پایانِ نما: این‌قدر بعد از در
const FADE_COLOR = '#f4f1ec';
const REVEAL_S = 4.6; // نمای «رفتن به گوشه و برگشتن به سمتِ کلِ اتاق»
const MAX_TOTAL_S = 38; // سقفِ طولِ فیلم با نماهای پیش‌فرض
const MAX_TOTAL_EXTRA_S = 62; // سقفِ مطلق (با نماهای اضافه)
const ENC_BITRATE = 2_200_000;

// نماهایی که کاربر در «پایان طراحی» انتخاب می‌کند (به همین ترتیب در فیلم می‌آیند). def: تیکِ پیش‌فرض (نماهای
// قبلیِ فیلم). d: طولِ تقریبیِ نماهای تکی (ثانیه)
export const FILM_ANGLES = [
  { id: 'aerial', label: 'پرواز پهپادی دورِ خانه', hint: 'شروعِ فیلم، سقف برداشته', def: true },
  { id: 'top', label: 'نمای کامل از بالا', hint: 'مثلِ نقشه، با چرخشِ آرام', def: false, d: 4.5 },
  { id: 'corners', label: 'نمای مورب از گوشه‌ها', hint: 'از ارتفاعِ کم، دورِ خانه', def: false, d: 5 },
  { id: 'flyover', label: 'پرواز از روی خانه', hint: 'از یک سر تا سرِ دیگر', def: false, d: 5.5 },
  { id: 'entrance', label: 'ورود از درِ اصلی', hint: 'دیدِ آدم، از بیرون به داخل', def: true },
  { id: 'doors', label: 'رفتن از درها به فضاهای دیگر', hint: 'عبور از در و بازشوها', def: true },
  { id: 'reveal', label: 'نگاهِ کامل به هر فضا', hint: 'از گوشه به کلِ اتاق', def: true },
  { id: 'reveal2', label: 'نگاهِ دوم به فضاهای بزرگ', hint: 'از گوشه‌ی روبه‌رو', def: true },
  { id: 'spin', label: 'چرخشِ ۳۶۰ درجه در فضای اصلی', hint: 'وسطِ بزرگ‌ترین فضا', def: false, d: 6.5 },
  { id: 'dolly', label: 'حرکتِ آرام رو به جلو', hint: 'در بزرگ‌ترین فضا', def: false, d: 4.5 },
  { id: 'window', label: 'نما از کنارِ پنجره‌ها', hint: 'رو به داخلِ اتاق', def: false, d: 4 },
  { id: 'outro', label: 'پرواز پهپادیِ پایانی', hint: 'برگشت به نمای اول', def: true },
];
export const DEFAULT_FILM_ANGLES = FILM_ANGLES.filter((a) => a.def).map((a) => a.id);
const angleLabel = (id) => (FILM_ANGLES.find((a) => a.id === id) || {}).label || id;
const FOV_AERIAL = 58;
const FOV_INSIDE = 76; // داخلِ خانه بازتر (قابِ عمودی باریک است)
const ease = (x) => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
};

export function pickVideoType() {
  if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined') return null;
  if (!HTMLCanvasElement.prototype.captureStream) return null;
  const candidates = [
    ['video/mp4;codecs=avc1.42E01F', 'mp4'],
    ['video/mp4;codecs=avc1', 'mp4'],
    ['video/mp4', 'mp4'],
    ['video/webm;codecs=vp9', 'webm'],
    ['video/webm;codecs=vp8', 'webm'],
    ['video/webm', 'webm'],
  ];
  for (const [type, ext] of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return { type, ext };
    } catch (e) {
      /* */
    }
  }
  return null;
}

function loadModel(glb, modelUrl) {
  const loader = new GLTFLoader();
  if (glb) return new Promise((resolve, reject) => loader.parse(glb, '', resolve, reject));
  return loader.loadAsync(modelUrl);
}

async function loadLightmap(blob, url) {
  const src = blob ? URL.createObjectURL(blob) : url;
  try {
    const tex = await new THREE.TextureLoader().loadAsync(src);
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.channel = 1;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
  } finally {
    if (blob) URL.revokeObjectURL(src);
  }
}

// لنگه‌های پنجره دیده شوند؛ لنگه‌های در (از کف شروع می‌شوند و بلندند) پنهان بمانند تا دوربین از وسطشان رد شود.
// جعبه‌ی لنگه‌های در = جای درها (اگر نقشه نبود، برای مسیرِ حرکت)
// درها: { box, obj, hinges: [{ o, base }] } — hinges همان گروه‌های لولادارِ داخلِ لنگه‌اند (هر کدام دورِ محورِ
// عمودیِ خودش می‌چرخد)؛ برای باز کردنِ خودکارِ بعضی درها جلوی دوربین
function setupLeaves(root) {
  const doors = [];
  const box = new THREE.Box3();
  const leaves = [];
  root.traverse((o) => {
    if (o.userData && o.userData.khLeaf) leaves.push(o);
  });
  root.updateMatrixWorld(true);
  leaves.forEach((o) => {
    o.visible = true;
    box.setFromObject(o);
    if (box.isEmpty()) return;
    const h = box.max.y - box.min.y;
    const fromFloor = box.min.y - 0; // نسبت به کفِ مبنا؛ اختلافِ سطحِ کف‌ها کوچک است
    if (h > 1.5 && fromFloor < 0.45) {
      o.visible = false;
      doors.push({ box: box.clone(), obj: o, hinges: o.children.map((c) => ({ o: c, base: c.rotation.y })) });
    }
  });
  return doors;
}

// اتاق‌ها از نقشه‌ی خام (سانتی‌متر → متر؛ y نقشه = z صحنه)
// هر فضا ممکن است چند تکه باشد (اتاقی که اتاقِ دیگری داخلش است، فضای L‌شکل): مستطیلِ اصلی = بزرگ‌ترین تکه
// (برای گوشه‌ها و مسیرِ دوربین)، ولی «کدام اتاق» با همه‌ی تکه‌ها سنجیده می‌شود
function roomsFromPlan(plan) {
  const rects = (plan && plan.scene && plan.scene.roomGroupRects) || [];
  const toM = (q) => ({
    x1: Math.min(q.x1, q.x2) / 100,
    x2: Math.max(q.x1, q.x2) / 100,
    z1: Math.min(q.y1, q.y2) / 100,
    z2: Math.max(q.y1, q.y2) / 100,
  });
  return rects
    .map((r) => {
      const pieces = (r.rects && r.rects.length ? r.rects : [r]).map(toM);
      const big = pieces.reduce((b, q) => ((q.x2 - q.x1) * (q.z2 - q.z1) > (b.x2 - b.x1) * (b.z2 - b.z1) ? q : b));
      const level = ((r.floorStyle && Number(r.floorStyle.levelCm)) || 0) / 100;
      const area = pieces.reduce((a, q) => a + (q.x2 - q.x1) * (q.z2 - q.z1), 0);
      return { ...big, pieces, level, cx: (big.x1 + big.x2) / 2, cz: (big.z1 + big.z2) / 2, area };
    })
    .filter((r) => r.area > 1 && (r.x2 - r.x1) > 1.3 && (r.z2 - r.z1) > 1.3)
    .sort((a, b) => a.area - b.area); // اتاقِ داخلی قبل از اتاقِ دورش پیدا شود
}


// درها و بازشوها از نقشه‌ی خام (همان محاسبه‌ی ویرایشگر) → { c (مرکز، متر)، n (عمود بر دیوار)، width، kind }
function passagesFromPlan(plan) {
  const sc = plan && plan.scene;
  if (!sc || !Array.isArray(sc.walls)) return [];
  const out = [];
  const add = (entry, kind, defaultWidth) => {
    const wall = sc.walls.find((w) => w.id === entry.wallId);
    if (!wall) return;
    const dx = wall.x2 - wall.x1;
    const dy = wall.y2 - wall.y1;
    const len = Math.hypot(dx, dy);
    if (!len) return;
    const ux = dx / len;
    const uy = dy / len;
    const width = (entry.style && entry.style.widthCm) || defaultWidth || 80;
    const half = width / 2 / len;
    const ratio = Math.max(half, Math.min(1 - half, Number(entry.ratio) || 0.5));
    const along = ratio * len;
    out.push({
      kind,
      width: width / 100,
      c: new THREE.Vector3((wall.x1 + ux * along) / 100, 0, (wall.y1 + uy * along) / 100),
      n: new THREE.Vector3(-uy, 0, ux),
    });
  };
  (sc.doors || []).forEach((d) => add(d, 'door', sc.doorWidth));
  (sc.openings || []).forEach((o) => add(o, 'opening', sc.openingWidth));
  return out;
}
function windowsFromPlan(plan) {
  const sc = plan && plan.scene;
  if (!sc || !Array.isArray(sc.walls) || !Array.isArray(sc.windows)) return [];
  return passagesFromPlan({ scene: { ...sc, doors: sc.windows.map((w) => ({ ...w })), openings: [], doorWidth: sc.windowWidth } }).map(
    (p) => ({ ...p, kind: 'window' })
  );
}

// اگر نقشه نبود: جای درها از لنگه‌های در (ضلعِ نازکِ جعبه = جهتِ عبور)
function passagesFromLeaves(doorBoxes) {
  return doorBoxes.map(({ box: b }) => {
    const size = b.getSize(new THREE.Vector3());
    const c = b.getCenter(new THREE.Vector3()).setY(0);
    return { kind: 'door', width: Math.max(size.x, size.z), c, n: size.x < size.z ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1) };
  });
}

const UP = new THREE.Vector3(0, 1, 0);
const roomAt = (rooms, p) =>
  rooms.find((r) => (r.pieces || [r]).some((q) => p.x >= q.x1 && p.x <= q.x2 && p.z >= q.z1 && p.z <= q.z2)) || null;

function clampInRoom(r, p, margin = 0.45) {
  if (!r) return p;
  p.x = Math.min(r.x2 - margin, Math.max(r.x1 + margin, p.x));
  p.z = Math.min(r.z2 - margin, Math.max(r.z1 + margin, p.z));
  return p;
}

// یک نمای «رد شدن از در» — جهت: به سمتِ فضایی که هنوز دیده نشده (اولویت با ورود از بیرون به داخل)
function makeClip(pass, rooms, seen) {
  const sideA = roomAt(rooms, pass.c.clone().addScaledVector(pass.n, 0.6));
  const sideB = roomAt(rooms, pass.c.clone().addScaledVector(pass.n, -0.6));
  if (!sideA && !sideB) return null;
  let dir = pass.n.clone(); // از B به A
  let from = sideB;
  let to = sideA;
  const score = (r) => (r ? (seen.has(r) ? 1 : 2) : 0); // بیرون = ۰ (هیچ‌وقت مقصد نیست اگر داخلی هست)
  if (score(sideB) > score(sideA)) {
    dir = dir.negate();
    from = sideA;
    to = sideB;
  }
  const level = Math.max(from ? from.level : 0, to ? to.level : 0);
  const y = level + EYE;
  const start = clampInRoom(from, pass.c.clone().addScaledVector(dir, -BEFORE_M)).setY((from ? from.level : level) + EYE);
  // تا دیوارِ روبه‌رو فاصله بماند: در اتاقِ کم‌عمق کمتر جلو می‌رود
  let after = AFTER_M;
  if (to) {
    const far = (v, lo, hi) => (v > 0 ? hi : lo);
    const ax = Math.abs(dir.x) > 1e-3 ? (far(dir.x, to.x1, to.x2) - pass.c.x) / dir.x : Infinity;
    const az = Math.abs(dir.z) > 1e-3 ? (far(dir.z, to.z1, to.z2) - pass.c.z) / dir.z : Infinity;
    const depth = Math.min(ax, az);
    if (Number.isFinite(depth)) after = Math.min(AFTER_M, Math.max(0.9, depth - 1.7));
  }
  const end = clampInRoom(to, pass.c.clone().addScaledVector(dir, after)).setY((to ? to.level : level) + EYE);
  const mid = pass.c.clone().setY(y);
  // نگاه: اول مستقیم از وسطِ در، آخرِ نما به سمتِ وسطِ فضای تازه (کمی پایین‌تر از افق)
  const lookStraight = (p) => p.clone().addScaledVector(dir, 3).setY(p.y - 0.15);
  let lookRoom = to ? new THREE.Vector3((to.x1 + to.x2) / 2, to.level + 1.0, (to.z1 + to.z2) / 2) : null;
  // فقط اگر وسطِ اتاق جلوی دوربین است، به سمتش بچرخد (نه به پشتِ سر)
  if (lookRoom && lookRoom.clone().sub(end).setY(0).dot(dir) < 0.3) lookRoom = null;
  const curve = new THREE.CatmullRomCurve3([start, mid, end], false, 'centripetal');
  // جهتِ چرخشِ آخرِ نما: سمتی از اتاق که فضای بیشتری دارد
  let panSide = 1;
  if (to) {
    const c = new THREE.Vector3((to.x1 + to.x2) / 2, 0, (to.z1 + to.z2) / 2);
    const right = new THREE.Vector3(-dir.z, 0, dir.x);
    panSide = c.sub(end).setY(0).dot(right) >= 0 ? -1 : 1;
  }
  return {
    to,
    end,
    d: CLIP_S,
    interior: true,
    f: (u) => {
      const s = u * u * (3 - 2 * u) * 0.85 + u * 0.15; // نرم ولی بی‌توقف
      const pos = curve.getPointAt(Math.min(1, s));
      const k = Math.max(0, (u - 0.55) / 0.45);
      const kk = k * k * (3 - 2 * k);
      let look = lookStraight(pos);
      if (lookRoom && end.distanceTo(lookRoom) > 0.8) {
        look = look.lerp(lookRoom, kk * 0.7);
      } else if (kk > 0) {
        // وسطِ اتاق جلو نیست (اتاقِ کم‌عمق): آخرِ نما آرام به سمتِ بزرگ‌ترِ اتاق بچرخد تا فضا دیده شود
        const yaw = panSide * kk * 0.65;
        const d = dir.clone().applyAxisAngle(UP, yaw);
        look = pos.clone().addScaledVector(d, 3).setY(pos.y - 0.15);
      }
      return { pos, look };
    },
  };
}

// «نگاهِ کامل» در یک اتاق: از نزدیکِ وسط به سمتِ گوشه‌ی k می‌رود (نگاه به همان گوشه)، و آخرِ راه برمی‌گردد و از
// همان گوشه به گوشه‌ی روبه‌رو — یعنی کلِ اتاق — نگاه می‌کند
function revealShot(r, k, tiltUp = false) {
  const m = 0.6;
  const cs = [
    [r.x1 + m, r.z1 + m],
    [r.x2 - m, r.z1 + m],
    [r.x2 - m, r.z2 - m],
    [r.x1 + m, r.z2 - m],
  ].map(([x, z]) => new THREE.Vector3(x, 0, z));
  const y = r.level + EYE;
  const c = new THREE.Vector3(r.cx, y, r.cz);
  const A = cs[k].setY(y);
  const O = cs[(k + 2) % 4].setY(y);
  const start = c.clone().lerp(A, 0.05);
  const end = c.clone().lerp(A, 0.9);
  const yawOf = (from, to) => Math.atan2(to.x - from.x, to.z - from.z);
  const yA = yawOf(start, A);
  const yO = yawOf(end, O);
  let dy = yO - yA;
  while (dy > Math.PI) dy -= Math.PI * 2;
  while (dy < -Math.PI) dy += Math.PI * 2;
  if (Math.abs(Math.abs(dy) - Math.PI) < 0.35) dy = Math.PI * (k % 2 ? 1 : -1) * 0.92;
  // در راه رفتن به گوشه، مستقیم به کنجِ دیوار خیره نشود: نگاه کمی به سمتِ داخلِ اتاق (دیوارِ کناری و
  // چیزهای کنارش) — بعد چرخشِ کامل تا گوشه‌ی روبه‌رو
  const yStart = yA - Math.sign(dy) * 0.95;
  dy += Math.sign(dy) * 0.95;
  return {
    d: REVEAL_S,
    interior: true,
    fov: FOV_INSIDE,
    room: r,
    end,
    f: (u) => {
      const pos = start.clone().lerp(end, ease(u / 0.6));
      const turn = ease((u - 0.35) / 0.58);
      const yaw = yStart + dy * turn;
      // بعضی نماها آخرِ چرخش کمی سر را بالا می‌برند (سقف، نورِ مخفی، ارتفاعِ فضا دیده شود)
      const up = tiltUp ? ease((u - 0.62) / 0.38) * 0.62 : 0;
      const look = pos.clone().add(new THREE.Vector3(Math.sin(yaw), -0.32 + 0.12 * turn + up, Math.cos(yaw)).multiplyScalar(3));
      return { pos, look };
    },
  };
}

// گوشه‌ای از اتاق که از نقطه‌ی p دورتر است (نما به سمتِ عمقِ اتاق برود)
function farCorner(r, p) {
  const cs = [
    [r.x1, r.z1],
    [r.x2, r.z1],
    [r.x2, r.z2],
    [r.x1, r.z2],
  ];
  let best = 0;
  let bd = -1;
  cs.forEach(([x, z], i) => {
    const d = p ? Math.hypot(x - p.x, z - p.z) : (i === 0 ? 1 : 0);
    if (d > bd) {
      bd = d;
      best = i;
    }
  });
  return best;
}

// برنامه‌ی کاملِ فیلم: t (ثانیه) → { pos, look, interior, fade }
function buildShot(root, plan, doorBoxes, angleIds) {
  const on = new Set(angleIds && angleIds.length ? angleIds : DEFAULT_FILM_ANGLES);
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const span = Math.max(4, size.x, size.z);
  const radius = span * 0.95;
  const height = box.max.y + span * 0.85;
  const look0 = new THREE.Vector3(center.x, box.min.y, center.z);
  const a0 = Math.PI * 0.25;
  const orbit = (a) => new THREE.Vector3(center.x + Math.cos(a) * radius, height, center.z + Math.sin(a) * radius);
  const aIntro = a0 + Math.PI * 0.55;

  const rooms = roomsFromPlan(plan);
  let passages = passagesFromPlan(plan);
  if (!passages.length) passages = passagesFromLeaves(doorBoxes);

  // ترتیب: اول درِ ورودی (یک طرفش بیرون است)، بعد هر بار نزدیک‌ترین در به پایانِ نمای قبلی
  const clips = [];
  const seen = new Set();
  const left = passages.slice();
  const isEntrance = (p) =>
    !roomAt(rooms, p.c.clone().addScaledVector(p.n, 0.6)) || !roomAt(rooms, p.c.clone().addScaledVector(p.n, -0.6));
  let cursor = null;
  while (left.length && clips.length < MAX_CLIPS) {
    let idx = 0;
    if (!cursor) {
      const e = left.findIndex(isEntrance);
      idx = e >= 0 ? e : 0;
    } else {
      let best = Infinity;
      left.forEach((p, i) => {
        const d = p.c.distanceTo(cursor);
        if (d < best) {
          best = d;
          idx = i;
        }
      });
    }
    const [pass] = left.splice(idx, 1);
    const clip = makeClip(pass, rooms, seen);
    if (!clip) continue;
    // اولین نما از درِ ورودی = «ورود از درِ اصلی»؛ بقیه = «رفتن از درها»
    clip.angle = !cursor && isEntrance(pass) ? 'entrance' : 'doors';
    if (!on.has(clip.angle)) {
      if (clip.to) seen.add(clip.to);
      cursor = clip.end;
      continue;
    }
    // یک در در میان: لنگه‌ی در جلوی دوربین خودکار باز می‌شود (بقیه از قبل برداشته شده‌اند تا راه باز باشد)
    if (pass.kind === 'door' && clips.length % 2 === 0) {
      const door = doorBoxes.find((d) => !d.anim && d.box.getCenter(new THREE.Vector3()).setY(0).distanceTo(pass.c) < 0.6);
      if (door && door.hinges.length) {
        door.anim = { clip, from: clip.f(0).pos };
        clip.door = door;
      }
    }
    if (clip.to) seen.add(clip.to);
    cursor = clip.end;
    clips.push(clip);
  }

  const segs = [];
  // بودجه‌ی زمان: نماهای اضافه (غیرِ پیش‌فرض) طولِ خودشان را به سقف اضافه می‌کنند
  const extraS = FILM_ANGLES.filter((a) => !a.def && on.has(a.id)).reduce((t, a) => t + (a.id === 'window' ? a.d * 2 : a.d), 0);
  const CAP = Math.min(MAX_TOTAL_EXTRA_S, MAX_TOTAL_S + extraS);
  const biggest = rooms.length ? rooms.reduce((b, r) => (r.area > b.area ? r : b)) : null;
  if (on.has('aerial'))
    segs.push({ angle: 'aerial', d: INTRO_S, interior: false, fov: FOV_AERIAL, f: (u) => ({ pos: orbit(a0 + (aIntro - a0) * u), look: look0 }) });
  // نمای کامل از بالا: دوربین تقریباً عمودی (کمی کج تا جهت معلوم باشد)، چرخشِ آرام
  if (on.has('top')) {
    const h = box.max.y + span * 1.75;
    segs.push({
      angle: 'top',
      d: 4.5,
      interior: false,
      fov: FOV_AERIAL,
      f: (u) => {
        const a = a0 + u * 0.7;
        return { pos: new THREE.Vector3(center.x + Math.cos(a) * span * 0.12, h, center.z + Math.sin(a) * span * 0.12), look: look0 };
      },
    });
  }
  // نمای مورب از گوشه‌ها: ارتفاعِ کم، یک چهارمِ دور
  if (on.has('corners')) {
    const h = box.max.y + span * 0.42;
    const rr = span * 0.78;
    const aC = a0 + Math.PI;
    segs.push({
      angle: 'corners',
      d: 5,
      interior: false,
      fov: FOV_AERIAL,
      f: (u) => {
        const a = aC + ease(u) * Math.PI * 0.5;
        return { pos: new THREE.Vector3(center.x + Math.cos(a) * rr, h, center.z + Math.sin(a) * rr), look: look0 };
      },
    });
  }
  // پرواز از روی خانه، در امتدادِ ضلعِ بلندتر
  if (on.has('flyover')) {
    const ax = size.x >= size.z ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
    const L = Math.max(size.x, size.z);
    const h = box.max.y + Math.max(2.2, span * 0.38);
    segs.push({
      angle: 'flyover',
      d: 5.5,
      interior: false,
      fov: FOV_AERIAL,
      f: (u) => {
        const s = -L * 0.62 + L * 1.1 * (u * 0.8 + ease(u) * 0.2);
        const pos = center.clone().addScaledVector(ax, s).setY(h);
        const look = center.clone().addScaledVector(ax, s + L * 0.35).setY(box.min.y);
        return { pos, look };
      },
    });
  }
  // نماهای داخل: بعد از ورود به هر اتاقِ تازه، «نگاهِ کامل» در همان اتاق (اتاق‌های بزرگ از دو گوشه)
  let used = (on.has('aerial') ? INTRO_S : 0) + (on.has('outro') ? OUTRO_S : 0) + segs.filter((g) => g.angle !== 'aerial').reduce((t, g) => t + g.d, 0);
  const revealed = new Set();
  let revealCount = 0;
  const add = (sg) => {
    if (used + sg.d > CAP) return false;
    segs.push(sg);
    used += sg.d;
    return true;
  };
  // بودجه‌ی زمان: اول یک «نگاهِ کامل» برای هر اتاق؛ هر چه ماند، نگاهِ دوم (از گوشه‌ی روبه‌رو) برای بزرگ‌ترها
  const roomsToShow = rooms.length;
  let spare = on.has('reveal')
    ? Math.floor((MAX_TOTAL_S - INTRO_S - OUTRO_S - clips.length * CLIP_S - roomsToShow * REVEAL_S) / REVEAL_S)
    : Math.floor((MAX_TOTAL_S - INTRO_S - OUTRO_S - clips.length * CLIP_S) / REVEAL_S);
  const doubles = new Set(
    rooms
      .filter((r) => r.area >= 12)
      .sort((a, b) => b.area - a.area)
      .filter(() => spare-- > 0)
  );
  const revealRoom = (r, from) => {
    if (!r || revealed.has(r)) return;
    revealed.add(r);
    const k = farCorner(r, from);
    revealCount += 1;
    if (on.has('reveal')) {
      const sg = revealShot(r, k, revealCount % 2 === 0);
      sg.angle = 'reveal';
      if (!add(sg)) return;
    }
    if (on.has('reveal2') && doubles.has(r)) {
      const sg = revealShot(r, (k + 2) % 4, true);
      sg.angle = 'reveal2';
      add(sg);
    }
  };
  clips.forEach((c) => {
    c.fov = FOV_INSIDE;
    if (!add(c)) return;
    revealRoom(c.to, c.end);
  });
  // اتاق‌هایی که از هیچ دری به آن‌ها نرسیدیم (به ترتیبِ بزرگی)
  rooms
    .slice()
    .sort((a, b) => b.area - a.area)
    .forEach((r) => revealRoom(r, null));
  // چرخشِ ۳۶۰ درجه وسطِ بزرگ‌ترین فضا
  if (on.has('spin') && biggest) {
    const pos = new THREE.Vector3(biggest.cx, biggest.level + EYE, biggest.cz);
    const y0 = 0;
    add({
      angle: 'spin',
      d: 6.5,
      interior: true,
      fov: FOV_INSIDE,
      f: (u) => {
        const yaw = y0 + (u * 0.85 + ease(u) * 0.15) * Math.PI * 2;
        return { pos, look: pos.clone().add(new THREE.Vector3(Math.sin(yaw), -0.28, Math.cos(yaw)).multiplyScalar(3)) };
      },
    });
  }
  // حرکتِ آرام رو به جلو در بزرگ‌ترین فضا (در امتدادِ ضلعِ بلندترش)
  if (on.has('dolly') && biggest) {
    const r = biggest;
    const alongX = r.x2 - r.x1 >= r.z2 - r.z1;
    const half = (alongX ? r.x2 - r.x1 : r.z2 - r.z1) / 2;
    const dir = alongX ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
    const c = new THREE.Vector3(r.cx, r.level + EYE, r.cz);
    const start = c.clone().addScaledVector(dir, -(half - 0.6));
    const end = c.clone().addScaledVector(dir, Math.min(0.4, half * 0.2));
    add({
      angle: 'dolly',
      d: 4.5,
      interior: true,
      fov: FOV_INSIDE,
      f: (u) => {
        const pos = start.clone().lerp(end, ease(u) * 0.7 + u * 0.3);
        return { pos, look: pos.clone().addScaledVector(dir, 3).setY(pos.y - 0.25) };
      },
    });
  }
  // نما از کنارِ پنجره‌ها (حداکثر دو پنجره، در فضاهای بزرگ‌تر)، رو به داخلِ اتاق با کمی حرکتِ کناری
  if (on.has('window')) {
    const wins = windowsFromPlan(plan)
      .map((w) => {
        const a = roomAt(rooms, w.c.clone().addScaledVector(w.n, 0.6));
        const b = roomAt(rooms, w.c.clone().addScaledVector(w.n, -0.6));
        const room = a || b;
        const inward = a ? w.n.clone() : w.n.clone().negate();
        return room ? { ...w, room, inward } : null;
      })
      .filter(Boolean)
      .sort((p, q) => q.room.area - p.room.area);
    const used2 = new Set();
    wins
      .filter((w) => {
        if (used2.has(w.room)) return false;
        used2.add(w.room);
        return true;
      })
      .slice(0, 2)
      .forEach((w) => {
        const r = w.room;
        const side = new THREE.Vector3(-w.inward.z, 0, w.inward.x);
        const base = clampInRoom(r, w.c.clone().addScaledVector(w.inward, 0.75)).setY(r.level + 1.45);
        const target = new THREE.Vector3(r.cx, r.level + 1.0, r.cz);
        add({
          angle: 'window',
          d: 4,
          interior: true,
          fov: FOV_INSIDE,
          f: (u) => {
            const pos = base.clone().addScaledVector(side, (ease(u) - 0.5) * 0.7);
            return { pos, look: target.clone().addScaledVector(side, (ease(u) - 0.5) * 0.9) };
          },
        });
      });
  }
  // فیلمی که هیچ نمایی نداشت (مثلاً فقط «ورود» تیک خورده ولی درِ ورودی پیدا نشد): همان پرواز پهپادی
  if (!segs.length && !on.has('outro'))
    segs.push({ angle: 'aerial', d: INTRO_S, interior: false, fov: FOV_AERIAL, f: (u) => ({ pos: orbit(a0 + (aIntro - a0) * u), look: look0 }) });
  const aOut = segs.length > 1 ? aIntro + Math.PI * 0.3 : aIntro;
  if (on.has('outro')) segs.push({
    angle: 'outro',
    d: OUTRO_S,
    interior: false,
    fov: FOV_AERIAL,
    f: (u) => ({ pos: orbit(aOut + (a0 + Math.PI * 2 - aOut) * (u * u * (3 - 2 * u) * 0.6 + u * 0.4)), look: look0 }),
  });

  // مرزِ نماها (برای برشِ نرم) — به‌جز ابتدا/انتهای فیلم که خودشان به هم می‌چسبند
  const bounds = [];
  let acc = 0;
  segs.forEach((sg, i) => {
    sg.t0 = acc;
    acc += sg.d;
    if (i < segs.length - 1) bounds.push(acc);
  });
  const total = acc;
  const segments = segs.map((sg) => ({ id: sg.angle || 'doors', label: angleLabel(sg.angle || 'doors'), t0: sg.t0, t1: sg.t0 + sg.d }));
  const at = (t) => {
    const tt = Math.min(Math.max(0, t), total - 1e-4);
    const sg = segs.find((x) => tt < x.t0 + x.d) || segs[segs.length - 1];
    const r = sg.f(Math.min(1, (tt - sg.t0) / sg.d));
    let fade = 0;
    bounds.forEach((b) => {
      const dt = Math.abs(tt - b);
      if (dt < FADE_S) fade = Math.max(fade, 1 - dt / FADE_S);
    });
    return { ...r, interior: sg.interior, fov: sg.fov || FOV_INSIDE, fade: fade * fade * (3 - 2 * fade) };
  };
  // درهای خودکار: زاویه‌ی هر لولا (۰ تا ۹۰ درجه) به سمتی که از دوربین دور می‌شود؛ قبل از نما بسته، در طولِ نما
  // (پیش از رسیدنِ دوربین) باز می‌شود و بعد باز می‌ماند
  const animDoors = doorBoxes.filter((d) => d.anim && d.anim.clip.t0 != null);
  animDoors.forEach((d) => {
    d.obj.visible = true;
    const from = d.anim.from.clone();
    const center = new THREE.Vector3();
    const tmp = new THREE.Box3();
    d.signs = d.hinges.map((h) => {
      const dist = (sign) => {
        h.o.rotation.y = h.base + sign * Math.PI * 0.5;
        h.o.updateMatrixWorld(true);
        tmp.setFromObject(h.o).getCenter(center);
        return center.distanceTo(from);
      };
      const sgn = dist(1) >= dist(-1) ? 1 : -1;
      h.o.rotation.y = h.base;
      h.o.updateMatrixWorld(true);
      return sgn;
    });
  });
  const doorsAt = (t) => {
    animDoors.forEach((d) => {
      const { t0, d: dur } = d.anim.clip;
      // در نمای پایانی آرام بسته می‌شوند تا تکرارِ فیلم (برگشت به اول) پرش نداشته باشد
      const k = ease(((t - t0) / dur - 0.04) / 0.38) * (1 - ease((t - (total - OUTRO_S)) / OUTRO_S));
      d.hinges.forEach((h, i) => {
        h.o.rotation.y = h.base + d.signs[i] * k * Math.PI * 0.48;
      });
    });
  };

  return { at, total, clips: clips.length, doorsAt, segments };
}

// WebCodecs: کدام کدک روی این دستگاه هست (H.264 اول — همه‌جا پخش می‌شود؛ VP9 فقط اگر H.264 نبود، سرور به H.264
// تبدیلش می‌کند)
async function pickEncoder() {
  if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') return null;
  const tries = [
    { codec: 'avc1.42E01F', kind: 'avc' },
    { codec: 'avc1.4D401F', kind: 'avc' },
    { codec: 'avc1.42E028', kind: 'avc' },
    { codec: 'avc1.640028', kind: 'avc' },
    { codec: 'vp09.00.31.08', kind: 'vp9' },
  ];
  for (const t of tries) {
    const config = {
      codec: t.codec,
      width: WIDTH,
      height: HEIGHT,
      bitrate: ENC_BITRATE,
      framerate: FPS,
      ...(t.kind === 'avc' ? { avc: { format: 'avc' } } : {}),
    };
    try {
      // eslint-disable-next-line no-await-in-loop
      const r = await VideoEncoder.isConfigSupported(config);
      if (r && r.supported) return { ...t, config: r.config || config };
    } catch (e) {
      /* بعدی */
    }
  }
  return null;
}

// فریم‌به‌فریم: هر فریم با زمانِ دقیقِ خودش (i/FPS) کشیده و فشرده می‌شود — مستقل از سرعتِ گوشی
async function encodeOffline(enc, frame, shot, canvas, onProgress) {
  const samples = [];
  let description = null;
  let failed = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      if (meta && meta.decoderConfig && meta.decoderConfig.description && !description) {
        const d = meta.decoderConfig.description;
        description = d instanceof ArrayBuffer ? d.slice(0) : d.buffer.slice(d.byteOffset, d.byteOffset + d.byteLength);
      }
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      samples.push({ data, key: chunk.type === 'key' });
    },
    error: (e) => {
      failed = e || new Error('encode-failed');
    },
  });
  encoder.configure(enc.config);
  const n = Math.max(1, Math.round(shot.total * FPS));
  // فریمِ کلیدی سرِ هر نما (باکس‌های کوچک دقیق و سریع به همان‌جا می‌پرند) و هر ۲ ثانیه
  const keyAt = new Set(shot.segments.map((g) => Math.round(g.t0 * FPS)));
  let lastYield = performance.now();
  for (let i = 0; i < n; i += 1) {
    if (failed) throw failed;
    frame(Math.min(i / FPS, shot.total));
    const vf = new VideoFrame(canvas, { timestamp: Math.round((i * 1e6) / FPS), duration: Math.round(1e6 / FPS) });
    encoder.encode(vf, { keyFrame: i === 0 || keyAt.has(i) || i % (FPS * 2) === 0 });
    vf.close();
    if (onProgress) onProgress(i / n);
    // صفِ فشرده‌سازی پر نشود (حافظه‌ی گوشی) و صفحه هم زنده بماند (پیش‌نمایش و درصد)
    while (encoder.encodeQueueSize > 4) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 4));
    }
    if (performance.now() - lastYield > 40) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => requestAnimationFrame(() => r()));
      lastYield = performance.now();
    }
  }
  await encoder.flush();
  encoder.close();
  if (failed) throw failed;
  if (!samples.length || (enc.kind === 'avc' && !description)) throw new Error('encode-empty');
  return muxMp4({ codec: enc.kind, width: WIDTH, height: HEIGHT, fps: FPS, description, samples });
}

export async function recordDesignFilm({ glb, modelUrl, lightmap, lightmapUrl, intensity = 1, plan, angles, onCanvas, onProgress }) {
  const enc = await pickEncoder();
  const video = enc ? null : pickVideoType();
  if (!enc && !video) return null;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: Boolean(enc) });
  renderer.setPixelRatio(1);
  renderer.setSize(WIDTH, HEIGHT, false);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#dfe6ee');
  scene.add(new THREE.HemisphereLight(0xffffff, 0xd6d0c4, 1.6)); // فقط برای شیشه و اشیای کوچکِ پخته‌نشده
  const camera = new THREE.PerspectiveCamera(62, WIDTH / HEIGHT, 0.05, 500);

  let root = null;
  const disposables = [];
  let lightMapTex = null;
  let recorder = null;
  let stream = null;
  try {
    const [gltf, lm] = await Promise.all([loadModel(glb, modelUrl), loadLightmap(lightmap, lightmapUrl)]);
    lightMapTex = lm;
    root = gltf.scene;
    const { ceilings } = prepareBakedScene(root, lightMapTex, intensity * Math.PI);
    const doors = setupLeaves(root);
    scene.add(root);
    // زمین زیرِ خانه (در نمای پهپادی)
    const box = new THREE.Box3().setFromObject(root);
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(Math.max(20, box.getSize(new THREE.Vector3()).length() * 2), 48),
      new THREE.MeshBasicMaterial({ color: '#d9d6cf' })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(box.getCenter(new THREE.Vector3()).x, box.min.y - 0.02, box.getCenter(new THREE.Vector3()).z);
    scene.add(ground);

    const shot = buildShot(root, plan, doors, angles);
    // برشِ نرم بینِ نماها: یک لایه‌ی روشن روی تصویر
    const fadeMat = new THREE.MeshBasicMaterial({ color: FADE_COLOR, transparent: true, opacity: 0, depthTest: false, depthWrite: false, toneMapped: false });
    const fadeQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), fadeMat);
    fadeQuad.frustumCulled = false;
    const fadeScene = new THREE.Scene();
    fadeScene.add(fadeQuad);
    const fadeCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const frame = (t) => {
      const { pos, look, interior, fade, fov } = shot.at(t);
      shot.doorsAt(t);
      if (camera.fov !== fov) {
        camera.fov = fov;
        camera.updateProjectionMatrix();
      }
      camera.position.copy(pos);
      camera.lookAt(look);
      ceilings.forEach((c) => {
        c.visible = interior; // نمای پهپادی بدونِ سقف، داخلِ خانه با سقف
      });
      renderer.render(scene, camera);
      if (fade > 0.001) {
        fadeMat.opacity = fade;
        renderer.autoClear = false;
        renderer.render(fadeScene, fadeCam);
        renderer.autoClear = true;
      }
    };
    disposables.push(fadeMat, fadeQuad.geometry);

    // گرم کردن: هر نما یک بار (وسطش) و هر دو حالتِ سقف، قبل از شروع — بافت‌ها و شیدرها از قبل روی کارتِ گرافیک
    // باشند تا اولین فریمِ هر نما کند نباشد
    ceilings.forEach((c) => {
      c.visible = true;
    });
    renderer.compile(scene, camera);
    shot.segments.forEach((g) => frame((g.t0 + g.t1) / 2));
    for (let i = 0; i < 3; i += 1) frame(0);
    shot.doorsAt(0);
    if (onCanvas) onCanvas(canvas);

    const segments = shot.segments.map((g) => ({ ...g, t0: Math.round(g.t0 * 100) / 100, t1: Math.round(g.t1 * 100) / 100 }));
    if (enc) {
      try {
        const blob = await encodeOffline(enc, frame, shot, canvas, onProgress);
        return { blob, type: 'video/mp4', ext: 'mp4', segments };
      } catch (e) {
        console.warn('[film] ساختِ فریم‌به‌فریم نشد، ضبطِ معمولی', e); // eslint-disable-line no-console
      }
    }
    const rec = video || pickVideoType();
    if (!rec) return null;

    stream = canvas.captureStream(FPS);
    recorder = new MediaRecorder(stream, { mimeType: rec.type, videoBitsPerSecond: BITRATE });
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };
    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = resolve;
      recorder.onerror = (e) => reject(e.error || new Error('record-failed'));
    });
    recorder.start(500);

    // زمانِ واقعی (فیلم همان سرعتی را دارد که ضبط شده)؛ فریمِ آخر = فریمِ اول تا تکرار بی‌پرش باشد
    await new Promise((resolve) => {
      const t0 = performance.now();
      const tick = () => {
        const t = (performance.now() - t0) / 1000;
        frame(Math.min(t, shot.total));
        if (onProgress) onProgress(Math.min(1, t / shot.total));
        if (t >= shot.total) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    frame(shot.total);
    await new Promise((r) => setTimeout(r, 120));
    recorder.stop();
    await stopped;
    const blob = new Blob(chunks, { type: rec.type.split(';')[0] });
    if (!blob.size) return null;
    return { blob, type: rec.type.split(';')[0], ext: rec.ext, segments };
  } finally {
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (e) {
        /* */
      }
    }
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (root) disposeTree(root);
    if (lightMapTex) lightMapTex.dispose();
    scene.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material && o.material.dispose) o.material.dispose();
    });
    disposables.forEach((d) => d.dispose());
    renderer.dispose();
    renderer.forceContextLoss();
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
  }
}
